import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'

export type TabCardItem = {
    href: string
    label: string
    description: string
    icon: ReactNode
    active: boolean
}

type TabCardsProps = {
    label: string
    items: TabCardItem[]
    className?: string
}

export const TabCards: FC<TabCardsProps> = ({ label, items, className }): ReactNode => {
    return (
        <nav aria-label={label} className={cn('mb-6 grid gap-3 print:hidden', items.length === 3 ? 'md:grid-cols-3' : 'sm:grid-cols-2', className)}>
            {items.map(item => (
                <Link
                    key={item.href}
                    href={item.href}
                    aria-current={item.active ? 'page' : undefined}
                    className={cn(
                        'group flex min-w-0 items-center gap-3 rounded-xl border p-4 transition-[background-color,border-color,box-shadow] outline-none focus-visible:ring-3 focus-visible:ring-ring/40',
                        item.active
                            ? 'border-primary/40 bg-primary/8 shadow-card ring-1 ring-primary/15'
                            : 'bg-card shadow-card hover:border-primary/30 hover:bg-accent/40'
                    )}
                >
                    <span
                        className={cn(
                            'grid size-10 shrink-0 place-items-center rounded-lg transition-colors [&_svg]:size-[18px]',
                            item.active ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground group-hover:text-primary'
                        )}
                        aria-hidden='true'
                    >
                        {item.icon}
                    </span>

                    <span className='min-w-0 flex-1'>
                        <strong className='block text-sm font-semibold text-foreground'>
                            {item.label}
                        </strong>

                        <span className='block truncate text-xs text-muted-foreground'>
                            {item.description}
                        </span>
                    </span>

                    <ArrowRight
                        className={cn('size-4 shrink-0 transition-transform', item.active ? 'text-primary' : 'text-muted-foreground group-hover:translate-x-0.5')}
                        aria-hidden='true'
                    />
                </Link>
            ))}
        </nav>
    )
}

type SegmentedTabsProps = {
    label: string
    items: { href: string, label: string, active: boolean }[]
}

export const SegmentedTabs: FC<SegmentedTabsProps> = ({ label, items }): ReactNode => {
    return (
        <nav aria-label={label} className='mb-4 inline-flex max-w-full gap-1 overflow-x-auto rounded-xl border bg-card p-1 shadow-card print:hidden'>
            {items.map(item => (
                <Link
                    key={item.href}
                    href={item.href}
                    aria-current={item.active ? 'page' : undefined}
                    className={cn(
                        'rounded-lg px-4 py-1.5 text-sm font-semibold whitespace-nowrap transition-colors outline-none focus-visible:ring-3 focus-visible:ring-ring/40',
                        item.active ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    )}
                >
                    {item.label}
                </Link>
            ))}
        </nav>
    )
}
